import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle, Loader2, RefreshCw, XCircle } from 'lucide-react';
import type {
  MatchConfidence,
  PricingRowDecision,
  PricingSheetRow,
  PricingSheetStatus,
  RoiGrade,
} from '@sourcito/schemas';
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle } from '../design';
import { ApiCallError } from '../lib/api';
import {
  useBulkDecide,
  useDecideRow,
  useMarkSheetReviewed,
  usePricingSheet,
  usePricingSheetRows,
} from '../lib/pricing-sheets';

const GRADE_OPTIONS: RoiGrade[] = ['A', 'B', 'C', 'D', 'F'];
const DECISION_OPTIONS: PricingRowDecision[] = ['pending', 'approved', 'skipped'];
const MATCH_OPTIONS: MatchConfidence[] = [
  'exact_asin',
  'exact_upc',
  'fuzzy_title_high',
  'fuzzy_title_medium',
  'manual',
  'unmatched',
];

const STATUS_LABEL: Record<PricingSheetStatus, string> = {
  parsing: 'Parsing',
  parsed: 'Parsed',
  parse_failed: 'Parse failed',
  reviewed: 'Reviewed',
};

function statusBadgeVariant(
  status: PricingSheetStatus
): 'neutral' | 'accent' | 'positive' | 'negative' {
  if (status === 'parsed') return 'accent';
  if (status === 'reviewed') return 'positive';
  if (status === 'parse_failed') return 'negative';
  return 'neutral';
}

function gradeBadgeVariant(
  g: RoiGrade | null | undefined
): 'neutral' | 'accent' | 'positive' | 'negative' {
  if (g === 'A' || g === 'B') return 'positive';
  if (g === 'C') return 'accent';
  if (g === 'D' || g === 'F') return 'negative';
  return 'neutral';
}

function matchLabel(c: MatchConfidence): string {
  switch (c) {
    case 'exact_asin':
      return 'ASIN';
    case 'exact_upc':
      return 'UPC';
    case 'fuzzy_title_high':
      return 'Title ↑';
    case 'fuzzy_title_medium':
      return 'Title ~';
    case 'manual':
      return 'Manual';
    case 'unmatched':
      return 'Unmatched';
  }
}

function matchBadgeVariant(c: MatchConfidence): 'neutral' | 'accent' | 'positive' | 'negative' {
  if (c === 'exact_asin' || c === 'exact_upc' || c === 'manual') return 'positive';
  if (c === 'fuzzy_title_high') return 'accent';
  if (c === 'fuzzy_title_medium') return 'neutral';
  return 'negative';
}

function decisionBadgeVariant(
  d: PricingRowDecision
): 'neutral' | 'accent' | 'positive' | 'negative' {
  if (d === 'approved') return 'positive';
  if (d === 'skipped') return 'negative';
  return 'neutral';
}

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

export function PricingSheetDetailPage() {
  const { id } = useParams({ from: '/pricing-sheets/$id' });

  const [gradeFilter, setGradeFilter] = useState<RoiGrade[]>([]);
  const [decisionFilter, setDecisionFilter] = useState<PricingRowDecision[]>([]);
  const [matchFilter, setMatchFilter] = useState<MatchConfidence[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const params = useMemo(
    () => ({
      grade: gradeFilter.length > 0 ? gradeFilter.join(',') : undefined,
      decision: decisionFilter.length > 0 ? decisionFilter.join(',') : undefined,
      match: matchFilter.length > 0 ? matchFilter.join(',') : undefined,
      limit: 200,
    }),
    [gradeFilter, decisionFilter, matchFilter]
  );

  const { data: sheet, isLoading: sheetLoading, isError, error } = usePricingSheet(id);
  const { data: rows, isLoading: rowsLoading } = usePricingSheetRows(id, params);
  const decide = useDecideRow(id);
  const bulkDecide = useBulkDecide(id);
  const markReviewed = useMarkSheetReviewed(id);

  const visibleIds = rows?.items.map((r) => r.id) ?? [];
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((rowId) => selected.has(rowId));

  function toggleRow(rowId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleIds));
    }
  }

  async function bulkAction(decision: PricingRowDecision) {
    if (selected.size === 0) return;
    await bulkDecide.mutateAsync({ rowIds: Array.from(selected), decision });
    setSelected(new Set());
  }

  async function approveAllGradeA() {
    const ids = (rows?.items ?? [])
      .filter((r) => r.roiGrade === 'A' && r.decision === 'pending')
      .map((r) => r.id);
    if (ids.length === 0) return;
    await bulkDecide.mutateAsync({ rowIds: ids, decision: 'approved' });
  }

  if (sheetLoading) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary p-8">
        <p className="text-sm text-fg-secondary">Loading…</p>
      </div>
    );
  }

  if (isError || !sheet) {
    const message =
      error instanceof ApiCallError && error.status === 404
        ? 'Pricing sheet not found.'
        : ((error as Error)?.message ?? 'Unknown error');
    return (
      <div className="min-h-screen bg-canvas text-fg-primary p-8">
        <Card className="border-negative/30 bg-negative/[0.06] max-w-2xl">
          <p className="text-sm text-negative">{message}</p>
        </Card>
      </div>
    );
  }

  const parsedPct =
    sheet.totalRows > 0 ? Math.round((sheet.matchedRows / sheet.totalRows) * 100) : 0;

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/suppliers/$id"
              params={{ id: sheet.supplierId }}
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ArrowLeft className="size-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-xl font-medium leading-tight truncate font-mono">
                {sheet.filename}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={statusBadgeVariant(sheet.status)}>
                  {STATUS_LABEL[sheet.status]}
                </Badge>
                <span className="text-xs text-fg-tertiary">
                  {sheet.totalRows} rows · {sheet.matchedRows} matched ({parsedPct}%) ·{' '}
                  {sheet.approvedRows} approved
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sheet.status === 'parsed' && (
              <Button
                onClick={() => markReviewed.mutate()}
                loading={markReviewed.isPending}
                leadingIcon={<CheckCircle className="size-4" />}
              >
                Mark sheet reviewed
              </Button>
            )}
            {sheet.status === 'reviewed' && <Badge variant="positive">Reviewed</Badge>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-6 space-y-5">
        {sheet.parseError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">{sheet.parseError}</p>
          </Card>
        )}

        {sheet.status === 'parsing' ? (
          <Card>
            <p className="flex items-center gap-2 text-sm text-fg-secondary">
              <Loader2 className="size-4 animate-spin" />
              Parsing in progress — refresh in a moment.
            </p>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Filters</CardTitle>
                <CardDescription>
                  Toggle pills to narrow the row list. "Approve all grade-A" picks the obvious wins
                  in one click.
                </CardDescription>
              </CardHeader>

              <div className="space-y-3">
                <FilterGroup
                  label="ROI grade"
                  options={GRADE_OPTIONS}
                  selected={gradeFilter}
                  onToggle={(v) => setGradeFilter((prev) => toggle(prev, v))}
                  variantFor={(g) => gradeBadgeVariant(g as RoiGrade)}
                  labelFor={(g) => g as string}
                />
                <FilterGroup
                  label="Match"
                  options={MATCH_OPTIONS}
                  selected={matchFilter}
                  onToggle={(v) => setMatchFilter((prev) => toggle(prev, v))}
                  variantFor={(m) => matchBadgeVariant(m as MatchConfidence)}
                  labelFor={(m) => matchLabel(m as MatchConfidence)}
                />
                <FilterGroup
                  label="Decision"
                  options={DECISION_OPTIONS}
                  selected={decisionFilter}
                  onToggle={(v) => setDecisionFilter((prev) => toggle(prev, v))}
                  variantFor={(d) => decisionBadgeVariant(d as PricingRowDecision)}
                  labelFor={(d) => d as string}
                />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={approveAllGradeA}
                    loading={bulkDecide.isPending}
                    leadingIcon={<CheckCircle className="size-4" />}
                  >
                    Approve all grade-A
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setGradeFilter([]);
                      setDecisionFilter([]);
                      setMatchFilter([]);
                    }}
                    leadingIcon={<RefreshCw className="size-4" />}
                  >
                    Clear filters
                  </Button>
                </div>
              </div>
            </Card>

            {selected.size > 0 && (
              <Card className="bg-accent-primary/[0.08] border-accent-primary/30">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-fg-primary">
                    {selected.size} row{selected.size === 1 ? '' : 's'} selected
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => bulkAction('approved')}
                      loading={bulkDecide.isPending}
                      leadingIcon={<CheckCircle className="size-4" />}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => bulkAction('skipped')}
                      loading={bulkDecide.isPending}
                      leadingIcon={<XCircle className="size-4" />}
                    >
                      Skip
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                      Clear
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Rows</CardTitle>
                <CardDescription>
                  {rowsLoading
                    ? 'Loading…'
                    : `${rows?.items.length ?? 0} shown / ${rows?.total ?? 0} match filters`}
                </CardDescription>
              </CardHeader>

              {rows && rows.items.length > 0 ? (
                <div className="overflow-x-auto -mx-4">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-subtle text-left text-fg-tertiary">
                        <th className="px-4 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleSelectAll}
                            aria-label="Select all"
                          />
                        </th>
                        <th className="px-4 py-2">ASIN / UPC</th>
                        <th className="px-4 py-2">Title</th>
                        <th className="px-4 py-2 text-right">Cost</th>
                        <th className="px-4 py-2 text-right">MOQ</th>
                        <th className="px-4 py-2">Match</th>
                        <th className="px-4 py-2">Grade</th>
                        <th className="px-4 py-2 text-right">ROI %</th>
                        <th className="px-4 py-2">Decision</th>
                        <th className="px-4 py-2 w-32">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.items.map((row) => (
                        <RowItem
                          key={row.id}
                          row={row}
                          selected={selected.has(row.id)}
                          onToggleSelect={() => toggleRow(row.id)}
                          onApprove={() => decide.mutate({ rowId: row.id, decision: 'approved' })}
                          onSkip={() => decide.mutate({ rowId: row.id, decision: 'skipped' })}
                          pending={decide.isPending}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-fg-secondary">No rows match the current filters.</p>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

interface FilterGroupProps<T extends string> {
  label: string;
  options: readonly T[];
  selected: T[];
  onToggle: (v: T) => void;
  variantFor: (v: T) => 'neutral' | 'accent' | 'positive' | 'negative';
  labelFor: (v: T) => string;
}

function FilterGroup<T extends string>({
  label,
  options,
  selected,
  onToggle,
  variantFor,
  labelFor,
}: FilterGroupProps<T>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-fg-tertiary w-16">{label}</span>
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors duration-fast ${
              active
                ? 'bg-accent-primary text-canvas'
                : 'bg-surface-2 text-fg-secondary hover:bg-surface-3'
            }`}
          >
            <Badge variant={variantFor(opt)} className={active ? 'bg-transparent text-canvas' : ''}>
              {labelFor(opt)}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}

interface RowItemProps {
  row: PricingSheetRow;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onSkip: () => void;
  pending: boolean;
}

function RowItem({ row, selected, onToggleSelect, onApprove, onSkip, pending }: RowItemProps) {
  return (
    <tr className="border-b border-border-subtle hover:bg-surface-1">
      <td className="px-4 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select row ${row.rowIndex + 1}`}
        />
      </td>
      <td className="px-4 py-2 font-mono text-xs">
        {row.asin ?? row.upc ?? <span className="text-fg-tertiary">—</span>}
      </td>
      <td className="px-4 py-2 max-w-xs truncate">{row.title ?? '—'}</td>
      <td className="px-4 py-2 text-right font-mono">
        {row.supplierPriceCents != null ? `$${(row.supplierPriceCents / 100).toFixed(2)}` : '—'}
      </td>
      <td className="px-4 py-2 text-right font-mono">{row.moq ?? '—'}</td>
      <td className="px-4 py-2">
        <Badge variant={matchBadgeVariant(row.matchConfidence)}>
          {matchLabel(row.matchConfidence)}
        </Badge>
      </td>
      <td className="px-4 py-2">
        {row.roiGrade ? (
          <Badge variant={gradeBadgeVariant(row.roiGrade)}>{row.roiGrade}</Badge>
        ) : (
          <span className="text-fg-tertiary">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-right font-mono">
        {row.estimatedRoiPct != null ? `${row.estimatedRoiPct.toFixed(1)}%` : '—'}
      </td>
      <td className="px-4 py-2">
        <Badge variant={decisionBadgeVariant(row.decision)}>{row.decision}</Badge>
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onApprove}
            disabled={pending || row.decision === 'approved'}
            className="text-xs text-positive hover:underline disabled:opacity-30"
          >
            Approve
          </button>
          <span className="text-fg-tertiary">·</span>
          <button
            type="button"
            onClick={onSkip}
            disabled={pending || row.decision === 'skipped'}
            className="text-xs text-negative hover:underline disabled:opacity-30"
          >
            Skip
          </button>
        </div>
      </td>
    </tr>
  );
}
